// API client. Every call goes to the *currently active* backend URL.
// Tab switch = swap `activeBackendUrl` (held in App state).

import type {
  AccountView,
  ActivityEntry,
  ActivityRollupResponse,
  AgentHistory,
  AgentView,
  BacklogCounts,
  BacklogPageView,
  BacklogTaskView,
  BackendsResponse,
  BatchingStatsWindowView,
  BlockedRuling,
  ChatMessage,
  ClaudeAccountWalletReconciliationView,
  ClaudeAccountWalletWindowReconciliationView,
  ConcurrencyCapKnobView,
  ConcurrencyCapView,
  DeepSeekTopupView,
  DeepSeekWalletReconciliationView,
  DispatchPauseStatusView,
  DeepSeekWalletWindowReconciliationView,
  EscalationView,
  FlatRateBoostReconciliationView,
  FlatRateBoostWindowReconciliationView,
  FleetFreezeStatusView,
  FleetGitHealthResponse,
  FromRole,
  GeminiAccountCapacityView,
  HostsCapacityResponse,
  HourlyUsageBucketView,
  NodeResourceHistoryResponse,
  LoginResponse,
  RoutingShadowComparisonRowView,
  RoutingShadowComparisonSummaryView,
  ModeInfo,
  MuseCapacityView,
  NvidiaCapacityView,
  ParkedTaskView,
  ReassignRequest,
  ResourceWatchdogStatus,
  RoleView,
  ScaleHostResponse,
  ModelDeathRatesView,
  ScheduledDispatchStatusView,
  ScheduledJobRunView,
  StateResponse,
  TaskUsageView,
  TaskUsageWindowView,
  TopTaskCostView,
  TranscriptEvent,
  UsageReport,
} from "./types";
import type { TranscriptSource } from "./transcriptTail";

class HttpError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body: string,
  ) {
    super(`${status} ${statusText}: ${body}`);
    this.name = "HttpError";
  }
}

// dashboard_frontend_state_fetch_hang_2026_07_30: plain fetch() has no default
// timeout, so a dropped/half-open connection (observed during a backend restart)
// leaves the Promise pending forever — refresh()'s Promise.all never resolves OR
// rejects, so neither the success path nor the existing 401-signout path ever
// fires, and the dashboard sits on "Fetching dashboard state" indefinitely with
// zero error surfaced. This bounds every request so a stall becomes a normal,
// visible error instead.
const REQUEST_TIMEOUT_MS = 20_000;

export class FetchTimeoutError extends Error {
  constructor(
    public path: string,
    public elapsedMs: number,
  ) {
    super(`Timed out after ${elapsedMs}ms waiting for ${path}`);
    this.name = "FetchTimeoutError";
  }
}

// Centralized model (2026-05-22): the dashboard talks to ONE backend — the
// central API. Per-VM interaction goes through that API's /api/vms/<id>/* proxy
// (the baseUrl carries the VM segment), so there is exactly ONE token: minted by
// logging into the central API, valid on every call. (The fleet shares one JWT
// secret from GCS, so even proxied calls validate.) The old per-backend token
// map is gone — there are no longer multiple direct backends to disambiguate.
let authToken: string | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

export function clearAuthToken(): void {
  authToken = null;
}

// Conditional-GET cache for the GET routes that emit an ETag (server/snapshot_etag.py:
// `/api/state` and the slot drill-down). Keyed by full URL, so a backend tab switch
// (`activeBackendUrl`) can never serve one backend's body for another's. A 304 carries no
// body, so the cached payload is returned verbatim — see httpRaw's 304 branch.
const etagCache = new Map<string, { etag: string; body: unknown }>();

/** One request, reporting whether the server answered `304 Not Modified`. `http()` is the
 * plain-`T` wrapper over this; only the callers that must act on "nothing changed"
 * (`stateProbe`, in snapshotStream.ts) need the flag. */
export async function httpRaw<T>(
  baseUrl: string,
  path: string,
  init?: RequestInit,
): Promise<{ data: T; notModified: boolean }> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init?.headers as Record<string, string> | undefined),
  };
  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`;
  }
  // GETs only — a conditional PUT/POST is meaningless, and reusing a GET's ETag on a
  // mutation would be a correctness bug.
  const isGet = (init?.method ?? "GET").toUpperCase() === "GET";
  const cacheKey = `${baseUrl}${path}`;
  const cached = isGet ? etagCache.get(cacheKey) : undefined;
  if (cached) headers["If-None-Match"] = cached.etag;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const startedAt = Date.now();
  let res: Response;
  try {
    // The dashboard's own session is now the Secure/HttpOnly/SameSite=Strict cookie
    // `set_session_cookie` sets on login (server/auth.py) — `credentials: "include"`
    // is what makes the browser attach it (and accept a fresh Set-Cookie) on both the
    // same-origin dev case and the prod split (dashboard SPA / api.* backend, same
    // registrable site) (ao_dashboard_jwt_localstorage_ws_query_string_unsandboxed_
    // iframe_2026_09_04.md todo 2). The Authorization header above still fires
    // whenever `authToken` is set in memory (e.g. this SPA session's own login), so
    // this is additive, not a replacement.
    res = await fetch(`${baseUrl}${path}`, { ...init, headers, credentials: "include", signal: controller.signal });
  } catch (e) {
    if (controller.signal.aborted) {
      throw new FetchTimeoutError(path, Date.now() - startedAt);
    }
    throw e;
  } finally {
    clearTimeout(timer);
  }
  // Checked BEFORE the !res.ok branch: 304 is not "ok" to fetch(), but it is the
  // success outcome of a conditional GET — the cached body is still current.
  if (res.status === 304 && cached) {
    return { data: cached.body as T, notModified: true };
  }
  if (!res.ok) {
    const body = await res.text();
    throw new HttpError(res.status, res.statusText, body);
  }
  // 204 No Content (e.g. DELETE /api/dispatch-holds/{id}) has no body to parse.
  if (res.status === 204) return { data: undefined as T, notModified: false };
  const data = (await res.json()) as T;
  const etag = res.headers.get("ETag");
  if (isGet && etag) {
    etagCache.set(cacheKey, { etag, body: data });
  }
  return { data, notModified: false };
}

async function http<T>(baseUrl: string, path: string, init?: RequestInit): Promise<T> {
  return (await httpRaw<T>(baseUrl, path, init)).data;
}

export { HttpError };

export const api = {
  state: (baseUrl: string) => http<StateResponse>(baseUrl, "/api/state"),
  resourceWatchdogStatus: (baseUrl: string) => http<ResourceWatchdogStatus>(baseUrl, "/api/resource-watchdog/status"),
  // Every AO-backend host (central + every v2 node) in one table row each — the
  // multi-host successor to the single-host /ws/vm-resources push. Polled, not
  // pushed: server/routes/hosts.py::list_hosts_capacity's own docstring on why this
  // stays disk-backed for central rather than a fresh snapshot() per poll.
  hostsCapacity: (baseUrl: string) => http<HostsCapacityResponse>(baseUrl, "/api/hosts/capacity"),
  // A host's durable resource-history series, for the expand-to-chart affordance.
  // Every host including central (see server/routes/hosts.py::get_host_resource_history's
  // own docstring for how central's series is read from its own JSONL log, not the
  // node-only node_resource_history table nodes use).
  hostResourceHistory: (baseUrl: string, hostId: string, withinHours: number = 24) =>
    http<NodeResourceHistoryResponse>(
      baseUrl,
      `/api/hosts/${encodeURIComponent(hostId)}/resource-history?within_hours=${withinHours}`,
    ),
  // R17 (ao_fleet_slot_resource_ui_and_scale_controls_2026_09_05.md todo 3) — set a
  // host's target active slot count. 404/409/422 carry a MEASURED refusal reason in
  // their JSON `detail` — see scaleHostErrorMessage in VmResources.tsx for how the
  // control surfaces that instead of a generic failure.
  scaleHost: (baseUrl: string, hostId: string, targetSlotCount: number) =>
    http<ScaleHostResponse>(baseUrl, `/api/hosts/${encodeURIComponent(hostId)}/slots/scale`, {
      method: "POST",
      body: JSON.stringify({ target_slot_count: targetSlotCount }),
    }),
  // Static-ish agent role registry — backs the read-only Registry tab.
  roles: (baseUrl: string) => http<RoleView[]>(baseUrl, "/api/roles"),
  // CI-failure + scheduled-dispatch escalations: up to 50 queued/dispatched/
  // unresolved from the last 24h, plus up to 10 resolved from the last 24h
  // (server/escalation.py::list_active_escalations — active_within_hours/
  // active_max query the active bucket SEPARATELY from the resolved one, so
  // one can't starve the other's slice of a shared window). This panel is
  // "what needs eyes now", not the full historical ledger — a wall stuck
  // queued past 24h stops appearing here (query the DB directly for that).
  // ao_escalations_dashboard_undercounts_true_queue_depth_2026_08_05: the OLD
  // unqualified 2h/no-cap call shared one LIMIT 100 across every status, so
  // once total churn exceeded 100 rows an older-but-still-queued row silently
  // vanished from the count — a 330-deep real backlog read as "100" here.
  // Powers the dashboard's Escalations rail panel.
  escalations: (baseUrl: string) =>
    http<EscalationView[]>(
      baseUrl,
      "/api/escalations/active?include_resolved_within_hours=24&active_within_hours=24&active_max=50",
    ),
  // Recent dispatch-attempt records for the 4 daily-scheduled planning-cleanup
  // jobs (server/state_store/scheduled_jobs.py). Powers the dashboard's
  // Scheduled Jobs rail panel — previously only visible via VM journalctl.
  scheduledJobRuns: (baseUrl: string) =>
    http<ScheduledJobRunView[]>(baseUrl, "/api/scheduled-jobs/recent?within_hours=48"),
  // Per-mode operator pause/resume for scheduled dispatch (plan_health / ci_reconcile
  // / etc.) — mirrors the existing per-slot pause/resume endpoints but keyed by
  // scheduled-task-type name rather than a numbered worker slot. Powers the
  // dashboard's Scheduled Dispatch rail panel.
  scheduledDispatchStatus: (baseUrl: string) =>
    http<ScheduledDispatchStatusView>(baseUrl, "/api/scheduled-dispatch/status"),
  pauseScheduledDispatch: (baseUrl: string, mode: string) =>
    http<{ mode: string; status: string }>(baseUrl, `/api/scheduled-dispatch/${encodeURIComponent(mode)}/pause`, {
      method: "POST",
    }),
  resumeScheduledDispatch: (baseUrl: string, mode: string) =>
    http<{ mode: string; status: string }>(baseUrl, `/api/scheduled-dispatch/${encodeURIComponent(mode)}/resume`, {
      method: "POST",
    }),
  // Fleet-wide "pause everything" toggle (operator ask 2026-08-30,
  // ao_fleet_pause_dispatch_ui_button_2026_08_30.md) — distinct from the
  // per-mode scheduled-dispatch pause above and the per-slot pause: this one
  // blocks pick_next_task fleet-wide AND kills every eligible slot's live
  // session in one action.
  dispatchPauseStatus: (baseUrl: string) => http<DispatchPauseStatusView>(baseUrl, "/api/fleet/dispatch-pause-status"),
  // Scheduled fleet-wide commit freeze (sit_promote_lease_scheduled_freeze_window_2026_08_29)
  // — read-only from the dashboard's side; armed/cleared by the hourly
  // sit-freeze-orchestrator.yml workflow, never by an operator click.
  fleetFreezeStatus: (baseUrl: string) => http<FleetFreezeStatusView>(baseUrl, "/api/fleet-freeze-status"),
  pauseFleetDispatch: (baseUrl: string, reason?: string) =>
    http<{ active: true; reason: string | null; paused_slot_ids: number[]; killed_sessions: Record<number, string> }>(
      baseUrl,
      `/api/fleet/pause-dispatch${reason ? `?reason=${encodeURIComponent(reason)}` : ""}`,
      { method: "POST" },
    ),
  resumeFleetDispatch: (baseUrl: string) =>
    http<{ active: false; resumed_slot_ids: number[] }>(baseUrl, "/api/fleet/resume-dispatch", { method: "POST" }),
  fleetGitHealth: (baseUrl: string, scope: "local" | "fleet" = "fleet") =>
    http<FleetGitHealthResponse>(baseUrl, `/api/fleet/git-health?scope=${scope}`),
  // Activity feed (Plan B): filters applied in SQL BEFORE the limit + `before_id`
  // cursor for "Load older". `types` is a comma-separated event-type set (the
  // category tabs pass their ALERT/OPS sets); `slot`/`type` are single-value filters.
  activity: (
    baseUrl: string,
    opts: {
      limit?: number;
      slot?: number;
      type?: string;
      types?: string;
      before_id?: number;
      since?: string;
      until?: string;
    } = {},
  ) => {
    const q = new URLSearchParams({ limit: String(opts.limit ?? 50) });
    if (opts.slot != null) q.set("slot", String(opts.slot));
    if (opts.type) q.set("type", opts.type);
    if (opts.types) q.set("types", opts.types);
    if (opts.before_id != null) q.set("before_id", String(opts.before_id));
    if (opts.since) q.set("since", opts.since);
    if (opts.until) q.set("until", opts.until);
    return http<ActivityEntry[]>(baseUrl, `/api/activity?${q}`);
  },
  // Denoise rollup — event_type counts within a window → the "×N in last 1h" badges.
  // dashboard_frontend_state_fetch_hang_2026_07_30: best-effort beacon fired when
  // http() times out (FetchTimeoutError) — persisted server-side via log_activity
  // so a recurrence is retroactively visible in /api/activity even though the
  // stalled tab itself never got a chance to report anything. Callers MUST NOT
  // await/surface failures from this — it's a courtesy, not load-bearing.
  reportStall: (baseUrl: string, report: { path: string; elapsed_ms: number; backend_url: string }) =>
    http<{ ok: boolean }>(baseUrl, "/api/client-telemetry/dashboard-stall", {
      method: "POST",
      body: JSON.stringify(report),
    }),
  activityRollup: (baseUrl: string, opts: { within_minutes?: number; by_slot?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (opts.within_minutes != null) q.set("within_minutes", String(opts.within_minutes));
    if (opts.by_slot) q.set("by_slot", "true");
    const qs = q.toString();
    return http<ActivityRollupResponse>(baseUrl, `/api/activity/rollup${qs ? `?${qs}` : ""}`);
  },
  backlog: (baseUrl: string, status?: string) =>
    http<BacklogTaskView[]>(baseUrl, status ? `/api/backlog?status=${status}` : "/api/backlog"),
  // Cursor-paginated variant of the above (ao_control_plane_read_models_and_
  // request_path_2026_09_04 [BACKEND] P0, audit P7) -- passing `limit` switches
  // the SAME backend route to the `{items, next_cursor}` envelope; `backlog()`
  // above is unaffected (it never passes cursor/limit, so it keeps getting the
  // bare array). Pass a prior call's `next_cursor` back as `cursor` to continue;
  // `next_cursor: null` means the last page was reached.
  backlogPage: (baseUrl: string, opts: { status?: string; tier?: number; cursor?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (opts.status) q.set("status", opts.status);
    if (opts.tier != null) q.set("tier", String(opts.tier));
    if (opts.cursor) q.set("cursor", opts.cursor);
    if (opts.limit != null) q.set("limit", String(opts.limit));
    const qs = q.toString();
    return http<BacklogPageView>(baseUrl, `/api/backlog${qs ? `?${qs}` : ""}`);
  },
  // Tab-badge counts without the ~5k-row body -- see BacklogCounts's own comment.
  backlogCounts: (baseUrl: string) => http<BacklogCounts>(baseUrl, "/api/backlog/counts"),
  taskBlockers: (baseUrl: string, taskId: string) =>
    http<{ task_id: string; explanation: string }>(baseUrl, `/api/backlog/${encodeURIComponent(taskId)}/blockers`),
  reloadBacklog: (baseUrl: string) =>
    http<{ ok: boolean; loaded?: number }>(baseUrl, "/api/backlog/reload", {
      method: "POST",
    }),

  // Backlog Integrity panel's "Fix" button (ao_backlog_collision_alert_and_
  // remediation_ui_2026_07_26 todo 4) — safe one-click remediation for an
  // unresolved sibling-reset-guard collision (todo 3's endpoint). 404s if the
  // collision was already resolved/superseded — callers should treat a 404 the
  // same as success (refetch and let the row drop).
  remintBacklogCollision: (baseUrl: string, taskId: string) =>
    http<{ ok: boolean; original_task_id: string; new_task_id: string; new_status: string }>(
      baseUrl,
      `/api/backlog/${encodeURIComponent(taskId)}/remint-collision`,
      { method: "POST" },
    ),

  // Parked-tasks surface (surface_parked_tasks_2026_07_25) — list + the two
  // dashboard actions ("Dispatch now" / operator-only "Mark done").
  parkedTasks: (baseUrl: string) => http<ParkedTaskView[]>(baseUrl, "/api/backlog/parked"),
  redispatchParkedTask: (baseUrl: string, taskId: string) =>
    http<{ task_id: string; condition: string; dispatchable: boolean }>(
      baseUrl,
      `/api/backlog/${encodeURIComponent(taskId)}/park/redispatch`,
      { method: "POST" },
    ),
  markParkedTaskDone: (baseUrl: string, taskId: string, note = "") =>
    http<{ task_id: string; status: string; condition: string }>(
      baseUrl,
      `/api/backlog/${encodeURIComponent(taskId)}/park/mark-done`,
      { method: "POST", body: JSON.stringify({ note }) },
    ),

  reloadPrompts: (baseUrl: string) =>
    http<{ ok: boolean; loaded: number; roles: string[] }>(baseUrl, "/api/prompts/reload", {
      method: "POST",
    }),

  spawnPreview: (
    baseUrl: string,
    params: {
      slot_id: number;
      server_url: string;
      account_id?: string;
      operator?: string;
      model?: string;
      effort?: string;
      thinking?: string;
    },
  ) => {
    const q = new URLSearchParams({
      slot_id: String(params.slot_id),
      server_url: params.server_url,
      ...(params.account_id ? { account_id: params.account_id } : {}),
      ...(params.operator ? { operator: params.operator } : {}),
      ...(params.model ? { model: params.model } : {}),
      ...(params.effort ? { effort: params.effort } : {}),
      ...(params.thinking ? { thinking: params.thinking } : {}),
    });
    return http<{ role: string; prompt: string }>(baseUrl, `/api/spawn/preview?${q}`);
  },

  spawnAgentPreview: (
    baseUrl: string,
    params: {
      role: string;
      server_url: string;
      machine?: string;
      rc_url?: string;
      model?: string;
      effort?: string;
      thinking?: string;
    },
  ) => {
    const q = new URLSearchParams({
      role: params.role,
      server_url: params.server_url,
      ...(params.machine ? { machine: params.machine } : {}),
      ...(params.rc_url ? { rc_url: params.rc_url } : {}),
      ...(params.model ? { model: params.model } : {}),
      ...(params.effort ? { effort: params.effort } : {}),
      ...(params.thinking ? { thinking: params.thinking } : {}),
    });
    return http<{ role: string; prompt: string }>(baseUrl, `/api/spawn/agent-preview?${q}`);
  },

  spawnSlot: (baseUrl: string, slotId: number, body: { account_id?: string; boot_prompt: string; cwd?: string }) =>
    http<{ ok: boolean; slot_id: number; tmux_session: string; attach_hint: string }>(
      baseUrl,
      `/api/slots/${slotId}/spawn`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  spawnAgent: (
    baseUrl: string,
    body: {
      role: string;
      label: string;
      // Required (Phase 4b-cleanup): server hard-fails with 400 if missing.
      // The named account's oauth_token_env_file is sourced into the spawned
      // claude session so the worker authenticates via setup-token, not the
      // ambient ~/.claude/.credentials.json.
      account_id: string;
      machine?: string;
      model?: string;
      effort?: string;
      thinking?: boolean;
      poll_interval_seconds?: number;
      boot_prompt: string;
    },
  ) =>
    http<{
      ok: boolean;
      agent_id: string;
      tmux_session: string;
      attach_hint: string;
      rc_url: string | null;
      rc_url_warning: string | null;
    }>(baseUrl, `/api/agents/spawn`, { method: "POST", body: JSON.stringify(body) }),

  // Drives `claude /usage` on the backend's machine and updates the account.
  // Slow (~10-15s) — show a spinner. Returns the fresh AccountView.
  refreshAccountUsage: (baseUrl: string, accountId: string) =>
    http<AccountView>(baseUrl, `/api/accounts/${encodeURIComponent(accountId)}/refresh-usage`, {
      method: "POST",
    }),

  backends: (baseUrl: string) => http<BackendsResponse>(baseUrl, "/api/backends"),
  healthz: (baseUrl: string) => http<{ status: string; ts: string }>(baseUrl, "/api/healthz"),

  setPrerequisite: (baseUrl: string, name: string, value: boolean, setBy = "operator") =>
    http<unknown>(baseUrl, `/api/prerequisites/${encodeURIComponent(name)}`, {
      method: "POST",
      body: JSON.stringify({ value, set_by: setBy }),
    }),

  answerBlocked: (baseUrl: string, blockedId: string, answer: string, fromRole: FromRole, ruling?: BlockedRuling) =>
    http<unknown>(baseUrl, `/api/blocked/${encodeURIComponent(blockedId)}/answer`, {
      method: "POST",
      body: JSON.stringify({
        answer,
        from_role: fromRole,
        ...(ruling
          ? {
              ruling_action: ruling.action,
              ...(ruling.action === "reclassify"
                ? { ruling_role: ruling.role }
                : { ruling_instruction: ruling.instruction }),
            }
          : {}),
      }),
    }),

  // `host` on all of the below (ao_operator_actions_ionos_node_slot_parity_2026_09_09):
  // disambiguates `slotId` across hosts the same way `slotLog`'s `host` already does — a
  // bare call only ever resolves CENTRAL's own identically-numbered slot. Omit for a
  // central slot; pass the slot's own `host` for a node slot.
  sendMessage: (baseUrl: string, slotId: number, text: string, fromRole: FromRole, host?: string | null) =>
    http<unknown>(baseUrl, `/api/slots/${slotId}/message`, {
      method: "POST",
      body: JSON.stringify({ text, from_role: fromRole, ...(host ? { host } : {}) }),
    }),

  reassign: (baseUrl: string, slotId: number, body?: ReassignRequest, host?: string | null) =>
    http<unknown>(baseUrl, `/api/slots/${slotId}/reassign`, {
      method: "POST",
      body: JSON.stringify({ ...(body ?? {}), ...(host ? { host } : {}) }),
    }),

  // ao_manual_account_switch_2026_08_07: operator-emergency lever, reachable
  // from a phone with no laptop/SSH access, to force a specific account onto
  // a live session — kills + resumes (context intact) rather than auto-picking.
  switchSlotAccount: (baseUrl: string, slotId: number, accountId: string, host?: string | null) =>
    http<{ slot_id: number; from_account: string | null; to_account: string; resumed: boolean }>(
      baseUrl,
      `/api/slots/${slotId}/switch-account`,
      { method: "POST", body: JSON.stringify({ account_id: accountId, ...(host ? { host } : {}) }) },
    ),

  switchRoleAccount: (baseUrl: string, role: string, accountId: string) =>
    http<{ role: string; from_account: string | null; to_account: string; resumed: boolean }>(
      baseUrl,
      `/api/agents/by-role/${encodeURIComponent(role)}/switch-account`,
      { method: "POST", body: JSON.stringify({ account_id: accountId }) },
    ),

  switchSlotModel: (baseUrl: string, slotId: number, model: string, host?: string | null) =>
    http<{ slot_id: number; from_model: string | null; to_model: string; resumed: boolean }>(
      baseUrl,
      `/api/slots/${slotId}/switch-model`,
      { method: "POST", body: JSON.stringify({ model, ...(host ? { host } : {}) }) },
    ),

  switchRoleModel: (baseUrl: string, role: string, model: string) =>
    http<{ role: string; from_model: string | null; to_model: string; resumed: boolean }>(
      baseUrl,
      `/api/agents/by-role/${encodeURIComponent(role)}/switch-model`,
      { method: "POST", body: JSON.stringify({ model }) },
    ),

  deleteSlot: (baseUrl: string, slotId: number, host?: string | null) =>
    http<{ ok: boolean; slot_id: number; killed_tmux?: string[] | null; status?: "pending_delete" }>(
      baseUrl,
      `/api/slots/${slotId}${host ? `?host=${encodeURIComponent(host)}` : ""}`,
      { method: "DELETE" },
    ),

  // `host` (ao_node_worker_slot1_stuck_and_parity_investigation_2026_09_04 deliverable
  // 2): disambiguates `slotId` across hosts -- a bare call only ever resolves CENTRAL's
  // own slot, since slot ids collide across hosts by design (SlotView.host's own
  // docstring). Omit for a central slot; pass the slot's own `host` for a node slot.
  slotLog: (baseUrl: string, slotId: number, historyLines = 200, host?: string | null) =>
    http<{
      slot_id: number;
      tmux_session: string | null;
      lines: string;
      available: boolean;
      source?: TranscriptSource;
      structured?: TranscriptEvent[] | null;
      // Node-hosted slots only: `truncated` = the node left older lines out to stay within
      // its reply budget; `transcript_id` = the node transcript file the content came from.
      truncated?: boolean;
      transcript_id?: string | null;
    }>(
      baseUrl,
      `/api/slots/${slotId}/log?history_lines=${historyLines}${host ? `&host=${encodeURIComponent(host)}` : ""}`,
    ),

  // Worker-tmux chat (Track 1, agent_orchestrator_mobile_and_worker_tmux_chat_2026_07_28):
  // synchronous, tmux-direct send — types straight into the slot's live pane via the
  // backend's tmux_spawn.submit_to_pane, NOT the async DB-queued /message path above.
  // Requires a real bearer token (server/auth.py's require_authenticated_user —
  // STRICT_AUTHED_DEPS, no anonymous/loopback bypass); the http() helper already attaches
  // Authorization on every call once logged in, so no extra wiring is needed here.
  sendMessageLive: (
    baseUrl: string,
    slotId: number,
    text: string,
    fromRole: FromRole = "operator",
    host?: string | null,
  ) =>
    http<{ slot_id: number; tmux_session: string; submitted: boolean; ok: boolean }>(
      baseUrl,
      `/api/slots/${slotId}/message-live`,
      { method: "POST", body: JSON.stringify({ text, from_role: fromRole, ...(host ? { host } : {}) }) },
    ),

  // Cursor-based incremental transcript read-back — the "read the reply" side of
  // worker-tmux chat. Pass the previous call's `next_offset` back as `sinceOffset` to
  // fetch only what's new since the last poll (never re-renders the whole file).
  // `host` -- same disambiguation as `slotLog` above; omit for a central slot.
  transcriptTail: (baseUrl: string, slotId: number, sinceOffset = 0, maxLines = 10000, host?: string | null) =>
    http<{
      slot_id: number;
      tmux_session: string | null;
      lines: string;
      structured_tail?: TranscriptEvent[] | null;
      next_offset: number;
      truncated?: boolean;
      available: boolean;
      // Only set for a node-hosted slot ("transcript_remote_node" = incremental transcript
      // read on the node; "tmux_remote_node" = a full pane SNAPSHOT to replace, not append —
      // see transcriptTail.ts). `transcript_id` names the node transcript file `next_offset`
      // is a cursor into.
      source?: TranscriptSource;
      transcript_id?: string | null;
    }>(
      baseUrl,
      `/api/slots/${slotId}/transcript-tail?since_offset=${sinceOffset}&max_lines=${maxLines}` +
        (host ? `&host=${encodeURIComponent(host)}` : ""),
    ),

  agentLog: (baseUrl: string, agentId: string, historyLines = 200) =>
    http<{
      agent_id: string;
      tmux_session: string | null;
      lines: string;
      available: boolean;
      source?: "transcript" | "tmux";
      structured?: TranscriptEvent[] | null;
    }>(baseUrl, `/api/agents/${encodeURIComponent(agentId)}/log?history_lines=${historyLines}`),

  // blocked_questions_ux_redesign_context_loss_and_scale_2026_07_24 (todo -002):
  // render the ASKING worker's transcript for a blocked question, keyed by the
  // claude_session_id captured at creation time (survives slot respawns).
  blockedTranscript: (baseUrl: string, blockedId: string, historyLines = 10000) =>
    http<{
      blocked_id: string;
      lines: string;
      available: boolean;
      source: "transcript" | null;
    }>(baseUrl, `/api/blocked/${encodeURIComponent(blockedId)}/transcript?history_lines=${historyLines}`),

  pause: (baseUrl: string, slotId: number, host?: string | null) =>
    http<unknown>(baseUrl, `/api/slots/${slotId}/pause${host ? `?host=${encodeURIComponent(host)}` : ""}`, {
      method: "POST",
    }),

  resume: (baseUrl: string, slotId: number, host?: string | null) =>
    http<unknown>(baseUrl, `/api/slots/${slotId}/resume${host ? `?host=${encodeURIComponent(host)}` : ""}`, {
      method: "POST",
    }),

  // Re-issue a live agent's /loop cadence (nudge, no respawn). seconds is bounded
  // server-side to [30, 3600]; the dashboard pre-clamps via clampLoopSeconds().
  setLoopInterval: (baseUrl: string, slotId: number, seconds: number) =>
    http<{ slot_id: number; seconds: number; delivered: boolean }>(baseUrl, `/api/slots/${slotId}/loop-interval`, {
      method: "POST",
      body: JSON.stringify({ seconds }),
    }),

  snapshot: (baseUrl: string, reason = "manual") =>
    http<unknown>(baseUrl, `/api/snapshot?reason=${encodeURIComponent(reason)}`, {
      method: "POST",
    }),

  // Manual dashboard (re)deploy (2026-09-03, deploy-dashboard.yml went workflow_dispatch-only
  // the same day) -- fires the GH workflow via server/routes/deploy_dashboard.py. No run id
  // comes back synchronously (a GitHub API limitation, not this endpoint's); runs_url is the
  // Actions page to watch instead.
  // `version` REQUIRED as of 2026-09-04 (ci_versioned_release_pipeline_integrity_and_speed_
  // 2026_09_04.md item 3) -- the versioned pipeline deploys a NAMED, already-built release
  // rather than rebuilding LDR HEAD on demand; the caller (DeployDashboardModal) must name one.
  deployDashboard: (baseUrl: string, version: string, target: "prod" | "uat" = "prod") =>
    http<{ dispatched: boolean; version: string; target: string; ref: string; runs_url: string }>(
      baseUrl,
      `/api/deploy-dashboard?version=${encodeURIComponent(version)}&target=${encodeURIComponent(target)}`,
      { method: "POST" },
    ),

  // Published versions for the deploy modal's dropdown (server/routes/deploy_dashboard.py's
  // GET /api/deploy-dashboard/releases) -- newest first, read from the same Artifact Registry
  // generic repo deploy-dashboard.yml's own manifest-verify step trusts.
  dashboardReleases: (baseUrl: string) =>
    http<{ releases: { version: string; create_time: string | null; currently_deployed_target: string | null }[] }>(
      baseUrl,
      "/api/deploy-dashboard/releases",
    ),

  login: (baseUrl: string, username: string, password: string, backendId?: string) =>
    http<LoginResponse>(baseUrl, "/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password, backend_id: backendId }),
    }),

  logout: (baseUrl: string) => http<unknown>(baseUrl, "/api/auth/logout", { method: "POST" }),

  // Silent session restore (App.tsx mount) -- the dashboard session is the
  // HttpOnly cookie alone (never persisted client-side), so a reload/deep-link
  // needs an authenticated call to re-derive a Session before falling back to
  // Login. A 401 (no valid cookie) is the normal "not logged in" outcome here,
  // not an error -- the caller catches it and shows Login.
  getSession: (baseUrl: string) => http<LoginResponse>(baseUrl, "/api/auth/session"),

  // One-use, 45s WebSocket ticket (server/routes/ops.py::mint_ws_ticket) — what a WS connect
  // URL carries instead of the raw bearer JWT (ao_dashboard_jwt_localstorage_ws_query_string_
  // unsandboxed_iframe_2026_09_04.md todo 4; see VmResources.tsx's useVmResources, the only
  // caller). Rides on the same cookie/Authorization auth as every other call in this file.
  mintWsTicket: (baseUrl: string) =>
    http<{ ticket: string; expires_at: string }>(baseUrl, "/api/ws-ticket", { method: "POST" }),

  listAccounts: (baseUrl: string) => http<AccountView[]>(baseUrl, "/api/accounts"),

  getTaskUsageWindows: (
    baseUrl: string,
    provider?: string,
    model?: string,
    roleGroup?: string,
    accountId?: string,
    excludeDiedMidTask?: boolean,
  ) => {
    const params = new URLSearchParams();
    if (provider) params.set("provider", provider);
    if (model) params.set("model", model);
    if (roleGroup) params.set("role_group", roleGroup);
    if (accountId) params.set("account_id", accountId);
    if (excludeDiedMidTask) params.set("exclude_died_mid_task", "true");
    const qs = params.toString();
    return http<TaskUsageWindowView[]>(baseUrl, `/api/backlog/usage/windows${qs ? `?${qs}` : ""}`);
  },

  // Per-model death rates (ao_death_reasons_gate_starvation_and_cost_columns_findings_
  // 2026_10_02 [BACKEND] P2): the transcript died_mid_task rate next to the activity
  // log's work-losing tmux_session_lost rate, so the flag's undercount is visible
  // rather than silent. Unfiltered by the panel's provider/model chips on purpose —
  // this IS the per-model breakdown, so slicing it would defeat it.
  getUsageDeathRates: (baseUrl: string, window = "7d") => {
    const params = new URLSearchParams();
    params.set("window", window);
    return http<ModelDeathRatesView>(baseUrl, `/api/backlog/usage/death-rates?${params.toString()}`);
  },

  // Per-task cost RANKING (anthropic_per_task_actual_spend_and_account_calibration_
  // 2026_08_10 [BACKEND] P2). Same filter set as getTaskUsageWindows so a task's rank
  // and the panel's window totals can never disagree about which rows they cover;
  // windowHours === null means lifetime.
  getTopTasksByCost: (
    baseUrl: string,
    windowHours?: number | null,
    limit?: number,
    provider?: string,
    model?: string,
    roleGroup?: string,
    accountId?: string,
    excludeDiedMidTask?: boolean,
  ) => {
    const params = new URLSearchParams();
    if (windowHours !== undefined && windowHours !== null) params.set("window_hours", String(windowHours));
    if (limit !== undefined) params.set("limit", String(limit));
    if (provider) params.set("provider", provider);
    if (model) params.set("model", model);
    if (roleGroup) params.set("role_group", roleGroup);
    if (accountId) params.set("account_id", accountId);
    if (excludeDiedMidTask) params.set("exclude_died_mid_task", "true");
    const qs = params.toString();
    return http<TopTaskCostView[]>(baseUrl, `/api/backlog/usage/top-tasks${qs ? `?${qs}` : ""}`);
  },

  getHourlyUsageSeries: (baseUrl: string, provider?: string, roleGroup?: string, start?: string, end?: string) => {
    const params = new URLSearchParams();
    if (provider) params.set("provider", provider);
    if (roleGroup) params.set("role_group", roleGroup);
    if (start) params.set("start", start);
    if (end) params.set("end", end);
    const qs = params.toString();
    return http<HourlyUsageBucketView[]>(baseUrl, `/api/backlog/usage/hourly-series${qs ? `?${qs}` : ""}`);
  },

  getBatchingStatsWindows: (
    baseUrl: string,
    provider?: string,
    model?: string,
    roleGroup?: string,
    accountId?: string,
  ) => {
    const params = new URLSearchParams();
    if (provider) params.set("provider", provider);
    if (model) params.set("model", model);
    if (roleGroup) params.set("role_group", roleGroup);
    if (accountId) params.set("account_id", accountId);
    const qs = params.toString();
    return http<BatchingStatsWindowView[]>(baseUrl, `/api/backlog/batching-stats/windows${qs ? `?${qs}` : ""}`);
  },

  // routing_decision_visibility_gap_2026_09_01 todo 3 — persisted per-tick
  // shadow-dispatch-comparison history (newest first).
  getRoutingShadowComparisonHistory: (baseUrl: string, limit?: number) => {
    const params = new URLSearchParams();
    if (limit) params.set("limit", String(limit));
    const qs = params.toString();
    return http<RoutingShadowComparisonSummaryView[]>(
      baseUrl,
      `/api/backlog/usage/shadow-comparison${qs ? `?${qs}` : ""}`,
    );
  },

  // Per-task shadow-comparison detail, computed ON DEMAND — never persisted (see
  // the summary route above for the durable tick-aggregate history).
  getRoutingShadowComparisonLive: (baseUrl: string, windowMinutes?: number, sampleLimit?: number) => {
    const params = new URLSearchParams();
    if (windowMinutes) params.set("window_minutes", String(windowMinutes));
    if (sampleLimit) params.set("sample_limit", String(sampleLimit));
    const qs = params.toString();
    return http<RoutingShadowComparisonRowView[]>(
      baseUrl,
      `/api/backlog/usage/shadow-comparison/live${qs ? `?${qs}` : ""}`,
    );
  },

  taskUsage: (baseUrl: string, taskId: string) =>
    http<TaskUsageView[]>(baseUrl, `/api/backlog/${encodeURIComponent(taskId)}/usage`),

  reportUsage: (baseUrl: string, accountId: string, report: UsageReport) =>
    http<AccountView>(baseUrl, `/api/accounts/${encodeURIComponent(accountId)}/usage`, {
      method: "POST",
      body: JSON.stringify(report),
    }),

  getDeepSeekWalletReconciliation: (baseUrl: string) =>
    http<DeepSeekWalletReconciliationView>(baseUrl, "/api/accounts/deepseek/wallet-reconciliation"),

  getDeepSeekWalletWindowReconciliation: (baseUrl: string, windowHours: number) =>
    http<DeepSeekWalletWindowReconciliationView>(
      baseUrl,
      `/api/accounts/deepseek/wallet-reconciliation/window?window_hours=${windowHours}`,
    ),

  getClaudeWalletReconciliation: (baseUrl: string) =>
    http<ClaudeAccountWalletReconciliationView[]>(baseUrl, "/api/accounts/claude/wallet-reconciliation"),

  getClaudeWalletWindowReconciliation: (baseUrl: string, windowHours: number) =>
    http<ClaudeAccountWalletWindowReconciliationView[]>(
      baseUrl,
      `/api/accounts/claude/wallet-reconciliation/window?window_hours=${windowHours}`,
    ),

  getFlatRateBoostReconciliation: (baseUrl: string) =>
    http<FlatRateBoostReconciliationView[]>(baseUrl, "/api/accounts/flat-rate-boost"),

  getFlatRateBoostWindowReconciliation: (baseUrl: string, windowHours: number) =>
    http<FlatRateBoostWindowReconciliationView[]>(
      baseUrl,
      `/api/accounts/flat-rate-boost/window?window_hours=${windowHours}`,
    ),

  getGeminiCapacity: (baseUrl: string) => http<GeminiAccountCapacityView[]>(baseUrl, "/api/accounts/gemini/capacity"),

  getNvidiaCapacity: (baseUrl: string) => http<NvidiaCapacityView | null>(baseUrl, "/api/accounts/nvidia/capacity"),

  getMuseCapacity: (baseUrl: string) => http<MuseCapacityView | null>(baseUrl, "/api/accounts/muse/capacity"),

  // Fleet-wide list (every configured per-account GLM/Anthropic cap plus the
  // fleet/project Gemini/Muse caps) — the ConcurrencyCapsPanel's data source.
  getConcurrencyCaps: (baseUrl: string) => http<ConcurrencyCapView[]>(baseUrl, "/api/accounts/concurrency-caps"),

  // The editable configuration behind those readings (operator ask 2026-09-18). A PUT
  // takes effect on the NEXT account pick -- the server invalidates its overlay snapshot
  // on write, so there is no TTL to wait out.
  getConcurrencyCapKnobs: (baseUrl: string) =>
    http<ConcurrencyCapKnobView[]>(baseUrl, "/api/accounts/concurrency-cap-knobs"),

  setConcurrencyCapKnob: (baseUrl: string, knob: string, value: number) =>
    http<ConcurrencyCapKnobView>(baseUrl, `/api/accounts/concurrency-cap-knobs/${encodeURIComponent(knob)}`, {
      method: "PUT",
      body: JSON.stringify({ value }),
    }),

  clearConcurrencyCapKnob: (baseUrl: string, knob: string) =>
    http<ConcurrencyCapKnobView>(baseUrl, `/api/accounts/concurrency-cap-knobs/${encodeURIComponent(knob)}`, {
      method: "DELETE",
    }),

  // Per-slot reading for the account bound to one slot, or null if unset/uncapped —
  // no hook reads this route since the sub-agent fan-out guard dropped its
  // cap check (2026-09-24); typed here for any slot-scoped dashboard consumer.
  getSlotConcurrencyCap: (baseUrl: string, slotId: number) =>
    http<ConcurrencyCapView | null>(baseUrl, `/api/slots/${slotId}/concurrency-cap`),

  listDeepSeekTopups: (baseUrl: string) => http<DeepSeekTopupView[]>(baseUrl, "/api/accounts/deepseek/topups"),

  recordDeepSeekTopup: (baseUrl: string, amountUsd: number, note?: string) =>
    http<DeepSeekWalletReconciliationView>(baseUrl, "/api/accounts/deepseek/topups", {
      method: "POST",
      body: JSON.stringify({ amount_usd: amountUsd, note: note || null }),
    }),

  recordDeepSeekOpeningBalance: (baseUrl: string, amountUsd: number, note?: string) =>
    http<DeepSeekWalletReconciliationView>(baseUrl, "/api/accounts/deepseek/wallet-reconciliation/opening-balance", {
      method: "POST",
      body: JSON.stringify({ amount_usd: amountUsd, note: note || null }),
    }),

  markRateLimited: (baseUrl: string, accountId: string, rateLimitedUntil: string, note?: string) =>
    http<AccountView>(baseUrl, `/api/accounts/${encodeURIComponent(accountId)}/rate-limited`, {
      method: "POST",
      body: JSON.stringify({ rate_limited_until: rateLimitedUntil, note }),
    }),

  clearRateLimit: (baseUrl: string, accountId: string) =>
    http<AccountView>(baseUrl, `/api/accounts/${encodeURIComponent(accountId)}/clear-rate-limit`, {
      method: "POST",
    }),

  // The server's DisableAccountRequest requires reason+actor (NEW-AO-01 audit trail) and 422s a
  // body-less POST. The caller supplies both: `actor` is the signed-in operator (session.user.name)
  // and `reason` is what they typed at the pause prompt, so the audit trail names a real person and
  // a real cause. `until` is omitted on purpose, which means an indefinite pause.
  disableAccount: (baseUrl: string, accountId: string, reason: string, actor: string) =>
    http<AccountView>(baseUrl, `/api/accounts/${encodeURIComponent(accountId)}/disable`, {
      method: "POST",
      body: JSON.stringify({ reason, actor }),
    }),

  enableAccount: (baseUrl: string, accountId: string) =>
    http<AccountView>(baseUrl, `/api/accounts/${encodeURIComponent(accountId)}/enable`, {
      method: "POST",
    }),

  // Correct the declared subscription tier. The server writes accounts.json (the SSOT
  // bootstrap re-reads at boot), not just the DB row, so the change survives a restart.
  updateAccountTier: (baseUrl: string, accountId: string, tier: string, weeklyMsgLimit?: number) =>
    http<AccountView>(baseUrl, `/api/accounts/${encodeURIComponent(accountId)}/tier`, {
      method: "POST",
      body: JSON.stringify({ tier, weekly_msg_limit: weeklyMsgLimit ?? null }),
    }),

  mode: (baseUrl: string) => http<ModeInfo>(baseUrl, "/api/mode"),

  // List agents (Plan B): filterable. Default EXCLUDES terminal (archived/finished)
  // so the live roster stays clean; pass include_finished=true (or status) to see PAST runs.
  // kind/lifecycle narrow by the two-axis classification; limit caps the result.
  listAgents: (
    baseUrl: string,
    opts: {
      status?: string;
      kind?: string;
      lifecycle?: string;
      include_finished?: boolean;
      limit?: number;
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (opts.status) q.set("status", opts.status);
    if (opts.kind) q.set("kind", opts.kind);
    if (opts.lifecycle) q.set("lifecycle", opts.lifecycle);
    if (opts.include_finished) q.set("include_finished", "true");
    if (opts.limit != null) q.set("limit", String(opts.limit));
    const qs = q.toString();
    return http<AgentView[]>(baseUrl, `/api/agents${qs ? `?${qs}` : ""}`);
  },

  agentHistoryByRole: (baseUrl: string, role: string, limit = 200) =>
    http<AgentHistory>(baseUrl, `/api/agents/by-role/${encodeURIComponent(role)}/history?limit=${limit}`),

  sendToRole: (baseUrl: string, role: string, text: string, fromRole: FromRole = "operator") =>
    http<ChatMessage>(baseUrl, `/api/agents/by-role/${encodeURIComponent(role)}/message`, {
      method: "POST",
      body: JSON.stringify({ text, from_role: fromRole }),
    }),

  promoteAgent: (baseUrl: string, agentId: string, targetRole: string) =>
    http<AgentView>(baseUrl, `/api/agents/${encodeURIComponent(agentId)}/promote`, {
      method: "POST",
      body: JSON.stringify({ target_role: targetRole }),
    }),

  archiveAgent: (baseUrl: string, agentId: string) =>
    http<AgentView>(baseUrl, `/api/agents/${encodeURIComponent(agentId)}/archive`, { method: "POST" }),

  /** Safety-checked cleanup for a STRANDED (non-current) holder — refuses (409) if the
   * record's own session is genuinely still alive, unlike archiveAgent above (an
   * unconditional "operator wants this stopped" kill switch for the CURRENT holder). */
  forceArchiveAgent: (baseUrl: string, agentId: string) =>
    http<AgentView>(baseUrl, `/api/agents/${encodeURIComponent(agentId)}/force-archive`, { method: "POST" }),

  restoreAgent: (baseUrl: string, agentId: string) =>
    http<AgentView>(baseUrl, `/api/agents/${encodeURIComponent(agentId)}/restore`, { method: "POST" }),

  deleteAgent: (baseUrl: string, agentId: string) =>
    http<{ ok: boolean; agent_id: string }>(baseUrl, `/api/agents/${encodeURIComponent(agentId)}`, {
      method: "DELETE",
    }),

  updateAgent: (
    baseUrl: string,
    agentId: string,
    fields: { label?: string; rc_url?: string; machine?: string; model?: string },
  ) =>
    http<AgentView>(baseUrl, `/api/agents/${encodeURIComponent(agentId)}/update`, {
      method: "POST",
      body: JSON.stringify(fields),
    }),

  adminPopulate: (baseUrl: string) =>
    http<{ ok: boolean; stdout: string[]; stderr: string[] }>(baseUrl, "/api/admin/populate", { method: "POST" }),

  adminReset: (baseUrl: string) =>
    http<{ ok: boolean; db_path: string; tasks_synced: number; accounts_synced: number }>(baseUrl, "/api/admin/reset", {
      method: "POST",
    }),
};
